import React from 'react';
import {Alert, AlertProps, Button, Col, Form, Input, Row} from 'antd';

import ErrorBoundary from '../components/ErrorBoundary';
import {getMemberMessages, messageType, showMessages} from '../helpers/message';
import {getLogo} from '../helpers/misc';
import {useGetCsrfCookieQuery, useLoginMutation} from '../store/api/authApi';

import './Login.less';

// This type is only here to reduce type errors while other parts of the codebase are updated to typescript.
type Carshare = 'regina' | 'cartogo'

const logo = getLogo();
const forgotPasswordUrl = window.env.BACKEND_WEB_URL + '/password/reset';

const Login = () => {
    const [form] = Form.useForm();
    const [login, loginResult] = useLoginMutation();
    useGetCsrfCookieQuery();

    const getMemberMessaging = () => {
        const messages = getMemberMessages();
        const clientName = window.env.CLIENT_NAME as Carshare;
        const clientMessages: {
            type: string;
            message: string;
            description: React.JSX.Element;
        }[] = messages[clientName];

        if (clientMessages !== undefined) {
            return clientMessages.map((message, index) => (
                <Alert
                    key={index}
                    type={message.type as AlertProps['type']}
                    showIcon
                    className="login-message"
                    message={message.message}
                    description={message.description}
                />
            ));
        }
    };

    React.useEffect(() => {
        if (loginResult.data) {
            document.cookie = `is_authenticated=1`
            window.location.replace('/');
        } else if (loginResult.error) {
            showMessages(
                "Authentication failed. Please check your email and password and try again.",
                messageType.error
            );
        }
    }, [loginResult.data, loginResult.error]);

    const handleFinish = (values: { password: string, email: string }) => {
        login({
            email: values["email"],
            password: values["password"]
        });
    };

    return (
        <ErrorBoundary>
            <div className="login-container">
                <div style={{margin: 0}}>
                    <div className="login-messages">
                        {getMemberMessaging()}
                    </div>
                    <Row
                        justify="space-around"
                        align="middle"
                        className={getMemberMessaging() && 'login-logo-row'}
                    >
                        <Col span={24}>
                            <img
                                src={logo}
                                className="login-logo"
                                alt="logo"
                            />
                        </Col>
                    </Row>
                    <Form
                        form={form}
                        name="personal-data"
                        layout="vertical"
                        onFinish={handleFinish}
                        requiredMark={false}
                        className={getMemberMessaging() && 'login-form-row'}
                    >
                        <Form.Item
                            name="email"
                            label="Email:"
                            rules={[{type: "string", whitespace: true, required: true, message: "'Email' is required"}]}
                        >
                            <Input name="email"/>
                        </Form.Item>
                        <Form.Item
                            name="password"
                            label="Password:"
                            rules={[{type: "string", whitespace: true, required: true, message: "'Password' is required"}]}
                        >
                            <Input.Password visibilityToggle name="password"/>
                        </Form.Item>
                        <Form.Item style={{textAlign: 'center'}}>
                            <Button type="primary" htmlType="submit">
                                Log In
                            </Button>
                        </Form.Item>
                    </Form>
                    <Row
                        justify="space-around"
                        align="middle"
                        className={getMemberMessaging() && 'login-submit-row'}
                    >
                            <a href={forgotPasswordUrl}>Forgot Password</a>
                    </Row>
                </div>
            </div>
        </ErrorBoundary>
    );
};

// Comment this library out to stop showing a warning in console, uncomment when we want to implement.
// import {withTranslation} from "react-i18next";
// export default withTranslation()(Login);

export default Login;
